import { Box, IconButton, ListItemIcon, ListItemText, Menu, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@material-ui/core";
import { makeStyles, Theme } from '@material-ui/core/styles';
import { MoreVert, Send, UnfoldLess, UnfoldMore } from "@material-ui/icons";
import { Button, Card, ConfirmationMenuItem, ConfirmDialog, DataGrid, FileUploadModal, filterProps, Paper, PaperBorderType, PDFPreview } from "@roc/ui";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { TierPricingEconomicsTableRow } from "./tierPricingEconomicsTableRow";
import { Alert } from "@material-ui/lab";
import EditPricingEconomicTierDialog from "./editPricingEconomicTierDialog";
import EditBorrowersFromTierDialog from "./editBorrowersFromTierDialog";
import EditBorrowerTierDialog from "./editBorrowerTierDialog";
import { CompanyProfileStore } from "../../stores/companyProfileStore";


const useStyles = makeStyles((theme: Theme) => ({
  noBorderCell: {
    border: 0,
  },
  borderCell: {
    border: '1px solid rgba(224, 224, 224, 1)'
  },
  table: {
    '& th': {
      borderBottom: '1px solid #eee'
    }
  },
  headerRow: {
    border: '1px solid #ccc',
    backgroundColor: '#f8f8f8',
    '& th': {
      padding: '8px'
    }
  }
}));

export type TierPricingEconomicsTableProps = {
  companyProfileStore: CompanyProfileStore
  emptyMessage: string;
  allowActions?: boolean;
  readOnly?: boolean
};

export const TierPricingEconomicsTable = observer((
  { emptyMessage, allowActions = false, readOnly = true, companyProfileStore }: TierPricingEconomicsTableProps) => {
  const { lenderTermTierEconomicsFormStore } = companyProfileStore
  const [expandAll, setExpandAll] = useState(false);
  const [editPricingEconomicTierDialog, setEditPricingEconomicTierDialog] = useState(false);
  const [editBorrowersFromTierDialog, setEditBorrowersFromTierDialog] = useState(false);
  const [editBorrowerTierDialog, setEditBorrowerTierDialog] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const classes = useStyles({});

  const openConfirmationDialog = (tierId) => {
    lenderTermTierEconomicsFormStore.setTermTierToDelete(tierId);
    setOpenConfirmationModal(true);
  }

  const openEditBorrowersFromTierDialog = (tierId) => {
    companyProfileStore.lenderTermTierEconomicsFormStore.resetBorrowers()
    lenderTermTierEconomicsFormStore.setTermTierToDelete(tierId);
    lenderTermTierEconomicsFormStore.setGetAllBorrowers(false)
    setEditBorrowersFromTierDialog(true);
  }

  const openEditBorrowerTierDialog = (borrowerId, oldTierId) => {
    lenderTermTierEconomicsFormStore.setBorrowerToDelete(borrowerId);
    lenderTermTierEconomicsFormStore.setTermTierToDelete(oldTierId);
    setEditBorrowerTierDialog(true);
  }

  const openEditPricingEconomicTierDialog = (tierId) => {
    lenderTermTierEconomicsFormStore.loadFromStore(tierId);
    lenderTermTierEconomicsFormStore.setTermTierToDelete(tierId);
    setEditPricingEconomicTierDialog(true);
  }

  const onConfirmDelete = () => {
    lenderTermTierEconomicsFormStore.deleteTermTier();
    setOpenConfirmationModal(false);
  }

  return (
    <>
      <Table>
        <TableHead>
          <TableRow className={classes.headerRow}>
            <TableCell></TableCell>
            <TableCell>Tier / Borrower Name</TableCell>
            <TableCell>Default Tier</TableCell>
            <TableCell>Disabled Pricing</TableCell>
            <TableCell>Yield Spreed Premium</TableCell>
            <TableCell>Origination Points</TableCell>
            <TableCell>Processing Fee</TableCell>
            <TableCell>Admin Fee</TableCell>
            <TableCell>Underwriting Fee</TableCell>
            <TableCell>Commitment Fee</TableCell>
            <TableCell>Buydown Fee</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {companyProfileStore.lenderTermTierEconomicsFormStore.termTierList.length == 0 ?
            <TableRow>
              <TableCell colSpan={10} className={classes.borderCell}>
                <Box textAlign={'center'}>
                  {emptyMessage}
                </Box>
              </TableCell>
            </TableRow>
            :
            companyProfileStore.lenderTermTierEconomicsFormStore.termTierList?.map((termTier, index) => (
              <TierPricingEconomicsTableRow
                lenderTermTierEconomicsFormStore={companyProfileStore.lenderTermTierEconomicsFormStore}
                openEditBorrowerTierDialog={openEditBorrowerTierDialog}
                openEditBorrowersFromTierDialog={openEditBorrowersFromTierDialog}
                openEditPricingEconomicTierDialog={openEditPricingEconomicTierDialog}
                openConfirmationDialog={openConfirmationDialog}
                termTier={termTier}
                index={index}
                expandAll={expandAll}
                allowActions={allowActions}
                readOnly={readOnly} />
            ))
          }
        </TableBody>
      </Table>

      <ConfirmDialog
        handleConfirm={() => onConfirmDelete()}
        handleClose={() => setOpenConfirmationModal(false)}
        handleCancel={() => setOpenConfirmationModal(false)}
        open={openConfirmationModal}
        body={<Alert severity="warning">
          Deleting this tier will remove all borrowers linked to it, and their terms will revert to the general term economics
        </Alert>}
        confirmButtonText="CONTINUE"
        cancelButtonText="CLOSE"
        title='Delete Tier' />

      <EditPricingEconomicTierDialog
        title=""
        open={editPricingEconomicTierDialog}
        companyProfileStore={companyProfileStore}
        handleClose={() => {
          setEditPricingEconomicTierDialog(false);
          companyProfileStore.lenderTermTierEconomicsFormStore.reset();
        }} />

      <EditBorrowersFromTierDialog
        title=""
        open={editBorrowersFromTierDialog}
        companyProfileStore={companyProfileStore}
        handleClose={() => setEditBorrowersFromTierDialog(false)} />

      <EditBorrowerTierDialog
        companyProfileStore={companyProfileStore}
        handleClose={() => setEditBorrowerTierDialog(false)}
        open={editBorrowerTierDialog}
        title="" />
    </>
  )
});
